import { Logo } from "./Logo";
import { ProjectSwitcher } from "./ProjectSwitcher";
import { InstallButton } from "./InstallButton";
import { useAuth } from "../context/AuthContext";
import { LogOut, Save, CheckCircle2, Loader2, LayoutGrid, CalendarDays, CalendarClock, Archive, LayoutDashboard, Wifi, WifiOff, CloudUpload } from "lucide-react";

export function Navbar({ saveState, online = true, pending = 0, onSave, view, onViewChange, activeDate, isToday, onJumpToday, projects, activeProjectId, onSwitchProject, onCreateProject, onRenameProject, onDeleteProject }) {
  const { user, logout } = useAuth();

  const tab = (id, label, Icon) => (
    <button
      data-testid={`tab-${id}`}
      onClick={() => onViewChange(id)}
      className={`flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-3 py-1.5 border transition-colors ${
        view === id ? "bg-[#2560C9] border-[#2560C9] text-white" : "border-[#2E3033] text-[#A0AABF] hover:bg-[#22252A]"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );

  return (
    <header className="h-16 shrink-0 border-b border-[#2E3033] bg-[#0A0A0A] flex items-center justify-between px-4 md:px-6">
      <div className="flex items-center gap-4">
        <Logo className="h-9" />
        <div className="hidden lg:block border-l border-[#2E3033] pl-4">
          <p className="font-heading font-black text-white text-sm tracking-tight leading-none">
            YARD ORGANIZER
          </p>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#6E7787] mt-0.5">
            Production Planner
          </p>
        </div>
        <div className="flex items-center gap-1.5 border-l border-[#2E3033] pl-4">
          {tab("planner", "Planner", LayoutGrid)}
          {tab("overview", "Overview", LayoutDashboard)}
          {tab("history", "History", CalendarDays)}
          {tab("bank", "Bank", Archive)}
        </div>
        {projects && projects.length > 0 && (
          <div className="border-l border-[#2E3033] pl-4">
            <ProjectSwitcher
              projects={projects}
              activeProjectId={activeProjectId}
              onSwitch={onSwitchProject}
              onCreate={onCreateProject}
              onRename={onRenameProject}
              onDelete={onDeleteProject}
            />
          </div>
        )}
        {activeDate && (
          <button
            data-testid="active-date-chip"
            onClick={onJumpToday}
            title={isToday ? "Editing today" : "Editing " + activeDate + " — click to jump to today"}
            className={`hidden md:flex items-center gap-1.5 border px-2.5 py-1.5 text-xs font-mono transition-colors ${
              isToday
                ? "border-[#2E3033] text-[#A0AABF]"
                : "border-[#F59E0B]/60 text-[#F59E0B] hover:bg-[#F59E0B]/10"
            }`}
          >
            <CalendarClock className="w-3.5 h-3.5" />
            {activeDate}
            {!isToday && <span className="uppercase tracking-wider text-[9px]">→ today</span>}
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <InstallButton />
        <div
          data-testid="connection-status"
          data-online={online ? "true" : "false"}
          title={online ? "Online" : "Offline — changes are saved on this device and will sync when you reconnect"}
          className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${
            online ? "border-[#10B981]/40 text-[#10B981]" : "border-[#F59E0B]/50 text-[#F59E0B] bg-[#F59E0B]/10"
          }`}
        >
          {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">{online ? "Online" : "Offline"}</span>
        </div>
        {pending > 0 && (
          <div
            data-testid="pending-sync-badge"
            title={`${pending} day${pending !== 1 ? "s" : ""} waiting to sync`}
            className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-[#2560C9]/50 text-[#2560C9] bg-[#2560C9]/10"
          >
            <CloudUpload className="w-3.5 h-3.5" /> {pending} pending
          </div>
        )}
        <div data-testid="save-status" className="flex items-center gap-1.5 text-xs font-mono text-[#6E7787] mr-1">
          {saveState === "saving" && (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2560C9]" /> <span className="hidden sm:inline">Saving…</span>
            </>
          )}
          {saveState === "saved" && (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" /> <span className="hidden sm:inline">Saved</span>
            </>
          )}
        </div>
        <button
          data-testid="manual-save-button"
          onClick={onSave}
          className="hidden sm:flex items-center gap-1.5 border border-[#2E3033] hover:bg-[#22252A] text-white text-sm px-3 py-1.5 transition-colors"
        >
          <Save className="w-4 h-4" /> Save
        </button>
        <span className="hidden xl:block text-xs font-mono text-[#A0AABF] truncate max-w-[160px]">
          {user?.email}
        </span>
        <button
          data-testid="logout-button"
          onClick={logout}
          className="flex items-center gap-1.5 border border-[#2E3033] hover:border-[#EF4444]/60 hover:text-[#EF4444] text-[#A0AABF] text-sm px-3 py-1.5 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span className="hidden sm:inline">Logout</span>
        </button>
      </div>
    </header>
  );
}
